// @flow
import { createSelector } from 'reselect';
import shallowequal from 'shallowequal';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import { selectPricingInfos } from '@gandi/billing';
import {
  selectDiscounts,
  selectProducts,
  type ProductId,
  type Product,
  type Discount,
} from '@gandi/cart';
import type { ReduxSelector } from '@gandi/flow-types';
import * as coupons from '@gandi/redux-resource-coupon';
import {
  instances,
  disks,
  type InstancePrice,
} from '@gandi/redux-resource-price';

import type { Dispatch, FullState, GetState } from '../../types';

const SHS50couponPrefixes = ['PAAS', 'SHS'];

// --------------------------------------------------------------------- Actions

export const fetchInstancePrices =
  (options: { ... } = {}): ((dispatch: Dispatch, getState: GetState) => any) =>
  (dispatch: Dispatch, getState: GetState) => {
    const pricingInfos = selectPricingInfos(getState());
    return dispatch(
      // $FlowFixMe[cannot-spread-inexact]
      instances.fetchAll({
        ...pricingInfos,
        ...options,
      }),
    );
  };

export const fetchDiskPrices =
  (options: { ... } = {}): ((dispatch: Dispatch, getState: GetState) => any) =>
  (dispatch: Dispatch, getState: GetState) => {
    const pricingInfos = selectPricingInfos(getState());
    return dispatch(
      // $FlowFixMe[cannot-spread-inexact]
      disks.fetchAll({
        ...pricingInfos,
        ...options,
      }),
    );
  };

export const selectSharedFeatures: ReduxSelector<
  FullState,
  void,
  InstancePrice['features'],
  // $FlowFixMe[incompatible-call]
> = createSelector(instances.selectPricesArray, instancePrices =>
  instancePrices[0]
    ? instancePrices[0].features.filter(feature =>
        instancePrices.every(
          price =>
            price.features.find(item => shallowequal(item, feature)) !==
            undefined,
        ),
      )
    : [],
);

export const selectSHSCartDiscount: (state: FullState) => Discount | void =
  createSelector([selectDiscounts], discounts =>
    discounts.find(discount =>
      SHS50couponPrefixes.find(prefix =>
        discount.code ? discount.code.startsWith(prefix) : false,
      ),
    ),
  );

export const selectOtherSHSProductDiscount: (
  state: FullState,
  ProductId: ?ProductId,
) => Product | void = createSelector(
  [selectProducts, productId => productId],
  (products, productId) => {
    const otherSHSProducts = products.filter(
      product =>
        product.process === 'create' &&
        product.productType === 'paas' &&
        (!productId || String(productId) !== String(product.id)),
    );

    const otherSHSProductDiscount = otherSHSProducts.find(product =>
      product.prices.find(price => price.is_discount),
    );

    return otherSHSProductDiscount;
  },
);

// ```
// - coupon available from billing/coupons API
// OR
// - coupon already in cart
//   AND
//   - no other SHS product benefiting from a discount
// ```
export const selectSHS50Eligible = (
  state: FullState,
  productId: ?ProductId,
): boolean => {
  const hasCartDiscount = selectSHSCartDiscount(state);
  const hasOtherSHSProductDiscount = selectOtherSHSProductDiscount(
    state,
    productId,
  );

  // $FlowFixMe[incompatible-call]
  const coupon: ?coupons.Coupon = SHS50couponPrefixes.reduce(
    (acc, prefix) =>
      acc ||
      // $FlowFixMe[prop-missing]
      coupons.selectOne(state, {
        consumed: 0,
        name: `${prefix}*`,
        code: `${prefix}50*`,
      }),
    null,
  );
  return !!(coupon || hasCartDiscount) && !hasOtherSHSProductDiscount;
};

export const showSHS50Discount = (
  state: FullState,
  productId: ?ProductId,
): boolean => {
  const isAuthenticated = selectIsAuthenticatedUser(state);

  return (
    (isAuthenticated && selectSHS50Eligible(state, productId)) ||
    !isAuthenticated
  );
};
